import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Timer, CheckCircle2, ChefHat, MessageSquareWarning } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLiveRefresh } from '@/contexts/WebSocketContext';
import { BackButton } from '@/components/BackButton';
import { API_BASE } from '@/utils/api';
import { useKitchenThresholds } from '@/components/kitchen/useKitchenThresholds';
import { kitchenLevel } from '@/theme/statusColors';

const COLS = [
  ['Confirmado', 'Pendientes', '#DC2626', 'En preparación', 'Empezar'],
  ['En preparación', 'En preparación', '#D97706', 'Listo', 'Listo'],
  ['Listo', 'Listos', '#2563EB', 'Entregado', 'Entregar'],
];
const LEVEL = { normal: 'text-muted-foreground', warning: 'text-[#92400e] dark:text-[#fbbf24]', critical: 'text-[#b91c1c] dark:text-[#f87171]' };
const mins = (iso, now) => Math.max(0, Math.floor((now - new Date(iso)) / 60000));

const useKitchenData = () => {
  const [orders, setOrders] = useState([]);
  const [prod, setProd] = useState([]);
  const load = useCallback(async () => {
    try {
      const [o, p] = await Promise.all([axios.get(`${API_BASE}/orders`), axios.get(`${API_BASE}/kitchen/suggested-production`)]);
      setOrders(o.data.filter(x => COLS.some(c => c[0] === x.status)));
      setProd(p.data.items || []);
    } catch { /* reintenta en el próximo ciclo */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, 60000);
  return { orders, prod, load };
};

const OrderCard = ({ order, col, now, t, canAdvance, onAdvance }) => {
  const level = col[0] === 'Listo' ? 'normal' : kitchenLevel(order.created_at, now, t);
  return (
    <article className={`m-card p-3 space-y-2 ${level === 'critical' ? 'ring-2 ring-[#DC2626]' : level === 'warning' ? 'ring-2 ring-[#D97706]' : ''}`} data-testid={`kitchen-order-${order.order_id}`}>
      <header className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-bold leading-tight">{order.unit_name || 'Mesa'}</h3>
          <p className="text-xs text-muted-foreground">#{order.order_id.slice(-4)}{order.participant_name ? ` · ${order.participant_name}` : ''}</p>
        </div>
        <span className={`inline-flex items-center gap-1 text-sm font-bold ${col[0] === 'Listo' ? 'text-[#166534] dark:text-[#4ade80]' : LEVEL[level]}`} data-testid={`kitchen-timer-${order.order_id}`}>
          {col[0] === 'Listo' ? <CheckCircle2 className="w-4 h-4" /> : <Timer className="w-4 h-4" />}
          {col[0] === 'Listo' ? 'Listo' : `${mins(order.created_at, now)} min`}
        </span>
      </header>
      <ul className="space-y-1">
        {(order.items || []).map((it, i) => (
          <li key={i} className="text-sm">
            <span className="font-bold mr-2">{it.quantity}</span>{it.product_name}{it.variant ? ` (${it.variant})` : ''}
            {it.notes && <span className="block mt-0.5 ml-5 w-max rounded bg-[#fde68a] text-[#78350f] text-xs font-bold px-1.5 py-0.5 uppercase">{it.notes}</span>}
          </li>
        ))}
      </ul>
      {order.notes && <p className="flex gap-1 rounded bg-[#fde68a] text-[#78350f] text-xs font-semibold p-1.5"><MessageSquareWarning className="w-4 h-4 shrink-0" />{order.notes}</p>}
      {canAdvance && (
        <button onClick={() => onAdvance(order, col[3])} data-testid={`kitchen-advance-${order.order_id}`}
          className={`w-full h-10 rounded-lg font-semibold text-white ${col[0] === 'Listo' ? 'bg-[#15803d] hover:bg-[#166534]' : 'bg-[#2563EB] hover:bg-[#1d4ed8]'}`}>{col[4]}</button>
      )}
    </article>
  );
};

export default function ModernKitchen({ tv = false }) {
  const { user } = useAuth();
  const t = useKitchenThresholds();
  const { orders, prod, load } = useKitchenData();
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 15000); return () => clearInterval(id); }, []);
  const advance = async (order, status) => {
    try { await axios.put(`${API_BASE}/orders/${order.order_id}/status`, { status }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo actualizar'); }
  };
  const canDeliver = user?.role !== 'kitchen';
  return (
    <div className={`${tv ? 'dark min-h-screen bg-[#0b1220] text-white p-6' : 'p-4 md:p-6'} space-y-4`} data-surface={tv ? 'dark' : undefined} data-testid={tv ? 'modern-kitchen-tv' : 'modern-kitchen'}>
      {tv && (
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-3"><BackButton className="text-white hover:bg-white/10" /><ChefHat className="w-8 h-8" /><span className="text-2xl font-bold">Cocina</span></div>
          <span className="text-4xl font-bold tabular-nums">{new Date(now).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</span>
        </header>
      )}
      <section className={`rounded-2xl p-3 ${tv ? 'bg-white/5' : 'bg-[#111827] text-white'}`} data-surface="dark" data-testid="suggested-production">
        <h2 className="text-sm font-semibold mb-2 text-slate-300">Producción sugerida (pedidos pendientes y en preparación)</h2>
        <div className="flex flex-wrap gap-2">
          {prod.slice(0, 8).map(p => (
            <span key={p.product_id || p.product_name} className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2" data-testid={`production-${p.product_id}`}>
              <span className="text-2xl font-bold">{p.quantity}</span><span className="text-sm">{p.product_name}</span>
            </span>
          ))}
          {!prod.length && <span className="text-sm text-slate-300">Nada pendiente</span>}
        </div>
      </section>
      <div className="grid md:grid-cols-3 gap-4">
        {COLS.map(col => {
          const list = orders.filter(o => o.status === col[0]).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          return (
            <section key={col[0]} className={`rounded-2xl p-3 space-y-3 ${tv ? 'bg-white/5' : 'bg-muted'}`} data-testid={`kitchen-col-${col[1]}`}>
              <h2 className="flex items-center gap-2 font-bold"><span className="w-3 h-3 rounded-full" style={{ background: col[2] }} />{col[1]} ({list.length})</h2>
              {list.map(o => <OrderCard key={o.order_id} order={o} col={col} now={now} t={t} canAdvance={col[0] !== 'Listo' || canDeliver} onAdvance={advance} />)}
            </section>
          );
        })}
      </div>
    </div>
  );
}
